import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Sparkles, TrendingUp, FileText, Zap, ArrowRight } from "lucide-react";

export default function Welcome() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isChecking, setIsChecking] = useState(true);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const company = JSON.parse(localStorage.getItem("company") || "{}");

  // Check if user has already seen welcome page
  useEffect(() => {
    const checkWelcomeStatus = async () => {
      try {
        const response = await apiClient.get("/auth/me");
        if (response.data.user.has_seen_welcome) {
          // User has already seen welcome, redirect to dashboard
          navigate("/dashboard");
          return;
        }
        setIsChecking(false);
      } catch (error) {
        console.error("Error checking welcome status:", error);
        setIsChecking(false);
      }
    };

    checkWelcomeStatus();
  }, [navigate]);

  // Handler functions defined before steps array
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const markWelcomeAsSeenAndNavigate = async () => {
    try {
      await apiClient.post("/auth/mark-welcome-seen");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error marking welcome as seen:", error);
      // Navigate anyway even if the API call fails
      navigate("/dashboard");
    }
  };

  const handleSkip = () => {
    markWelcomeAsSeenAndNavigate();
  };

  const features = [
    {
      icon: Sparkles,
      title: "AI-Powered Generation",
      description: "Create professional job requisitions in minutes with our advanced AI that understands your industry and company culture."
    },
    {
      icon: TrendingUp,
      title: "Market Intelligence",
      description: "Get real-time salary data and competitive insights to ensure your requisitions are market-competitive."
    },
    {
      icon: FileText,
      title: "Smart Customization",
      description: "Tone customization and cultural alignment make every requisition unique to your company."
    },
    {
      icon: Zap,
      title: "Instant Export",
      description: "Export to PDF, Word, or plain text. Share with hiring teams instantly."
    }
  ];

  const steps = [
    {
      title: "Welcome to SmartReqs!",
      content: (
        <div className="flex gap-6 items-start">
          {/* Left Side - Hero Image */}
          <div className="w-1/2 rounded-lg overflow-hidden shadow-xl">
            <img 
              src="https://customer-assets.emergentagent.com/job_req-builder-5/artifacts/xpqrv5r0_Screenshot%202025-11-02%20at%209.29.36%E2%80%AFAM.png"
              alt="Professional using SmartReqs"
              className="w-full h-full object-cover"
            />
          </div>

          {/* Right Side - Welcome Text */}
          <div className="w-1/2 space-y-6">
            <div className="text-center space-y-4">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900">
                Welcome, {user.email?.split('@')[0] || 'there'}! 🎉
              </h2>
              <p className="text-lg text-slate-600">
                Thank you for subscribing to SmartReqs for <span className="font-semibold text-blue-600">{company.name || 'your company'}</span>
              </p>
              <p className="text-slate-500">
                You're all set to create professional, AI-powered job requisitions that stand out in the market.
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-slate-900 mb-2">What's Next?</h3>
              <p className="text-slate-700">
                Let's take a quick tour to show you how SmartReqs will transform your hiring process.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Powerful Features at Your Fingertips",
      content: (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Everything You Need to Hire Better
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-blue-300 transition-colors">
                <CardHeader>
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <feature.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                      <CardDescription className="mt-2">
                        {feature.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Pro Tip:</strong> Start with your most urgent role to see the power of AI-generated requisitions!
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Ready to Get Started?",
      content: (
        <div className="space-y-6 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto">
            <Zap className="w-10 h-10 text-white" />
          </div>
          
          <h2 className="text-3xl font-bold text-slate-900">
            You're All Set!
          </h2>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Your SmartReqs account is ready. Start creating professional job requisitions that attract top talent.
          </p>

          {/* Tour Image */}
          <div className="max-w-2xl mx-auto my-6" style={{ width: '70%' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_resume-reqfix/artifacts/ib75wahz_5_Tour%20Page%203_Pic.jpg" 
              alt="SmartReqs Platform Overview"
              className="w-full h-auto rounded-lg shadow-lg"
            />
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 rounded-lg max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold mb-4">Quick Start Guide:</h3>
            <div className="text-left space-y-3">
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-white text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                <p>Click "Create New Requisition" from your dashboard</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-white text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <p>Fill in job details and select your industry & tone</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-white text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <p>Click "Generate with AI" and watch the magic happen</p>
              </div>
              <div className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 bg-white text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                <p>Review, edit, and export your professional requisition</p>
              </div>
            </div>
          </div>

          <p className="text-sm text-slate-500">
            Need help? Check out our support resources in the dashboard.
          </p>
        </div>
      )
    }
  ];

  // Show loading while checking welcome status
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* SEO H1 - Screen reader accessible */}
      <h1 className="sr-only">Welcome to SmartReqs - AI-Powered Job Requisition Builder</h1>
      
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-slate-200 z-50">
        <div
          className="h-full bg-gradient-to-r from-blue-600 to-purple-600 transition-all duration-300"
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        />
      </div>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Skip Button */}
        <div className="flex justify-end mb-4">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-slate-600 hover:text-slate-900"
          >
            Skip Tour
          </Button>
        </div>

        {/* Main Content Card */}
        <Card className="shadow-2xl border-2">
          <CardContent className="p-8 md:p-12">
            {/* Current Step Content */}
            <div className="min-h-[400px]">
              {steps[currentStep].content}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-12 pt-8 border-t">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className="min-w-[100px]"
              >
                Previous
              </Button>

              {/* Step Indicators */}
              <div className="flex space-x-2">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentStep
                        ? "bg-blue-600 w-8"
                        : index < currentStep
                        ? "bg-blue-400"
                        : "bg-slate-300"
                    }`}
                  />
                ))}
              </div>

              {currentStep < steps.length - 1 ? (
                <Button
                  onClick={handleNext}
                  className="min-w-[100px] bg-blue-600 hover:bg-blue-700"
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={markWelcomeAsSeenAndNavigate}
                  className="min-w-[140px] bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          Step {currentStep + 1} of {steps.length}
        </div>
      </div>
    </div>
  );
}
